import { guidV4 } from '../generate/guid';

/**
 * @module frames/windowOpen
 */

/**
 * Opens a new window (popup) with the given dimensions and given URL at the
 * center of the current open window.
 *
 * @param {string} url - Url to open the window to
 * @param {number} width - width of the window in pixels
 * @param {number} height - height of the window in pixels
 * @returns {Window} The window created by the function
 */
export function windowOpenCentered(url, width, height) {
    const left = window.screenX + (window.innerWidth / 2) - (width / 2);
    const top = window.screenY + (window.innerHeight / 2) - (height / 2);

    const newWindow = window.open(url, '', `top=${top},left=${left},height=${height},width=${width},location=no,resizable=yes,toolbar=no,scrollbars=yes,status=no,menubar=no`);

    if (newWindow.focus) {
        newWindow.focus();
    }

    return newWindow;
}

export const pickerPurpose = {
    Upload: 0,
    LinkToCloud: 1,
    Combined: 2
};

/**
 * Opens a window with a linking file picker allowing the user to select files, resolves
 * once the user has selected their files. Supports:
 * <p>- Platform - GoogleDrivePicker.aspx (Or IntermediatePage with 'GoogleDrivePicker'</p>
 * <p>- Platform - OneDriveFilePicker.aspx (Or IntermediatePage with 'OneDriveFilePicker'</p>
 *
 * @param {string} pickerUrl - Page url for the picker to launch
 * @param {boolean} [selectMultiple=true] - Allow the user to select multiple files
 * @param {boolean} [allowedFileExtensions=null] - File extensions whitelist
 * @param {number} [width=660] - Width of the picker window
 * @param {number} [height=550] - Height of the picker window
 * @returns {Promise} { files: [{ id, name, url, mimeType, size, permissions, capabilities }], oAuthContext: { oAuthToken } }
 *
 * Result files picked by the user
 */
export function openLinkingFilePicker(
    pickerUrl,
    selectMultiple = true,
    allowedFileExtensions = null,
    width = 660,
    height = 550
) {
    return openFilePicker(pickerUrl, pickerPurpose.LinkToCloud, selectMultiple, allowedFileExtensions, width, height);
}

/**
 * Opens a window with a file picker allowing the user to select files, resolves
 * once the user has selected their files. Supports:
 * <p>- Platform - GoogleDrivePicker.aspx (Or IntermediatePage with 'GoogleDrivePicker'</p>
 * <p>- Platform - OneDriveFilePicker.aspx (Or IntermediatePage with 'OneDriveFilePicker'</p>
 *
 * @param {string} pickerUrl - Page url for the picker to launch
 * @param {number} purpose - Purpose for the picker
 * @param {boolean} [selectMultiple=true] - Allow the user to select multiple files
 * @param {boolean} [allowedFileExtensions=null] - File extensions whitelist
 * @param {number} [width=660] - Width of the picker window
 * @param {number} [height=550] - Height of the picker window
 * @returns {Promise} { files: [{ id, name, url, mimeType, size, permissions, capabilities }], oAuthContext: { oAuthToken } }
 *
 * Result files picked by the user
 */
export function openFilePicker(
    pickerUrl,
    purpose,
    selectMultiple = true,
    allowedFileExtensions = null,
    width = 660,
    height = 550
) {
    const url = new URL(pickerUrl);
    const target = guidV4();

    // The target is used as an identifier on an event fired when the user
    // finishes selecting their files
    url.searchParams.append('target', target);
    url.searchParams.append('mode', JSON.stringify({
        allowToSelectMultipleFiles: selectMultiple,
        allowedFileExtensions,
        allowCollaboration: false,
        purpose: purpose,
    }));

    windowOpenCentered(url.href, width, height);

    return new Promise((resolve, reject) => {
        // Listen for the event with the target guid supplied to the picker window
        window.addEventListener('message', (event) => {
            if (event.data && event.data.target === target) {
                resolve(event.data);
            }
        });
    });
}

/**
 * Opens the url in a window centered on the screen and returns a Promise
 *
 * @export
 * @param {string} url - Url to open the window to
 * @param {number} [width=660] - width of the window in pixels
 * @param {number} [height=550] - height of the window in pixels
 * @returns {Promise} - Resolves when the window closes
 */
export function windowOpen(url, width = 660, height = 550) {
    const window = windowOpenCentered(url, width, height);

    return new Promise((resolve, reject) => {
        const isWindowClosed = setInterval(function () {
            if (window && window.closed) {
                clearInterval(isWindowClosed);
                resolve('window closed');
            }
        }, 500);
    });
}
